import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import {
    HandlingPartySelect,
    type HandlingPartySelectQueryState,
} from '@client/components/_shared/selectors/common/HandlingPartySelect';
import { InsurerSelect } from '@client/components/_shared/selectors/common/InsurerSelect';
import { ProductTypeSelect } from '@client/components/_shared/selectors/products/ProductTypeSelect';
import { SupplierSelect } from '@client/components/_shared/selectors/suppliers/SupplierSelect';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { RangePicker } from '@client/components/_ui/data-entry/RangePicker';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useIsTrainingManagerOnly } from '@client/hooks/auth/useIsTrainingManagerOnly';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { capture } from '@client/utils/analytics';
import { REPORT_TYPE_MODULE_MAP, REPORT_TYPES, type ReportType } from '@shared/constants/business/report';
import { ORG_ROLES } from '@shared/constants/business/user';
import { hasPermission } from '@shared/permissions/roles';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Col, Form, Row, Select, Spin, Switch } from 'antd';
import { useId, useState } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute('/console/$orgId/reporting/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Generate a new report',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReportAdd />
        </PageController>
    );
}

type Report = RouterInputs['report']['generateReport'];

const FormItem = Form.Item<Report>;

/**
 * Noun spliced into the handling-parties field copy ("Report on complaints handled by").
 * Staff training types are intentionally absent — parties filter staff there, not records
 * "handled by" them — so they fall back to the static label.
 */
const REPORT_TYPE_HANDLED_NOUN: Partial<Record<ReportType, string>> = {
    [REPORT_TYPES.CLAIMS]: 'claims',
    [REPORT_TYPES.CLAIMS_BORDEREAU]: 'claims',
    [REPORT_TYPES.CLAIMS_GICOP_BREACH]: 'breaches',
    [REPORT_TYPES.COMPLAINTS]: 'complaints',
    [REPORT_TYPES.COMPLAINTS_ASIC_2023]: 'complaints',
    [REPORT_TYPES.INCIDENTS]: 'incidents',
    [REPORT_TYPES.IBCCC_BREACH_2025]: 'breaches',
};

function generateLastTenPeriodsForAsicReport() {
    const periods: string[] = [];
    const currentDate = new Date();

    let currentYear = currentDate.getFullYear();
    let currentPeriod = currentDate.getMonth() < 6 ? 1 : 2;

    for (let i = 0; i < 10; i++) {
        periods.unshift(`${currentPeriod}/${currentYear}`);

        if (currentPeriod === 1) {
            currentPeriod = 2;
            currentYear--;
        } else {
            currentPeriod = 1;
        }
    }

    return periods;
}

function useReportTypeOptions() {
    const org = useOrg();
    const isBordereauReportEnabled = useFeatureFlag('is-bordereau-report-enabled');
    const isTrainingManagerOnly = useIsTrainingManagerOnly();

    const options: {
        module: (typeof org.enabledModules)[number];
        label: string;
        /** When set, this group is only shown for training manager-only users */
        trainingOnly?: boolean;
        options: {
            label: string;
            value: string;
        }[];
    }[] = [
        {
            module: 'claim',
            label: 'Claims',
            options: [
                {
                    label: 'Claims',
                    value: REPORT_TYPES.CLAIMS,
                },
                ...(isBordereauReportEnabled
                    ? [
                          {
                              label: 'Claims - Bordereau',
                              value: REPORT_TYPES.CLAIMS_BORDEREAU,
                          },
                      ]
                    : []),
                {
                    label: 'Claims - GICOP breaches',
                    value: REPORT_TYPES.CLAIMS_GICOP_BREACH,
                },
            ],
        },
        {
            module: 'compliance',
            label: 'Complaints',
            options: [
                {
                    label: 'Complaints',
                    value: REPORT_TYPES.COMPLAINTS,
                },
                {
                    label: 'Complaints - ASIC 2023',
                    value: REPORT_TYPES.COMPLAINTS_ASIC_2023,
                },
            ],
        },
        {
            module: 'compliance',
            label: 'Incidents',
            options: [
                {
                    label: 'Incidents',
                    value: REPORT_TYPES.INCIDENTS,
                },
                {
                    label: 'IBCCC 2025 breach data report',
                    value: REPORT_TYPES.IBCCC_BREACH_2025,
                },
            ],
        },
        {
            module: 'compliance',
            label: 'Training',
            trainingOnly: true,
            options: [
                {
                    label: 'Staff training summary report',
                    value: REPORT_TYPES.STAFF_TRAINING_SUMMARY,
                },
                {
                    label: 'Staff training detailed report',
                    value: REPORT_TYPES.STAFF_TRAINING_DETAILED,
                },
            ],
        },
        {
            module: 'risk',
            label: 'Risk',
            options: [
                {
                    label: 'Risk',
                    value: REPORT_TYPES.RISK,
                },
            ],
        },
    ];

    return options
        .filter((item) => org.enabledModules.includes(item.module))
        .filter((item) => !isTrainingManagerOnly || item.trainingOnly)
        .flatMap((item) => ({
            label: item.label,
            options: item.options,
        }));
}

function ReportAdd() {
    const orgId = useOrgId();
    const formId = useId();
    const reportTypeOptions = useReportTypeOptions();
    const isTrainingManagerOnly = useIsTrainingManagerOnly();
    const { trpcUtils } = useTrpc();
    const { fDateShortDayJs } = useFormatter();
    const { mutate, isPending } = trpc.report.generateReport.useMutation();
    const navigate = useNavigate();

    const user = useUser();
    const [form] = Form.useForm<Report>();
    const type = Form.useWatch(({ type }) => type, form);
    const handlingPartyIds = Form.useWatch(
        (values) => ('handlingPartyIds' in values ? values.handlingPartyIds : undefined),
        form,
    );
    const handledNoun = type ? REPORT_TYPE_HANDLED_NOUN[type] : undefined;

    const isRegularUserTrainingReport =
        !hasPermission(user.roles, 'org:admin') &&
        !user.roles.includes(ORG_ROLES.TRAINING_MANAGER) &&
        (type === 'STAFF_TRAINING_SUMMARY' || type === 'STAFF_TRAINING_DETAILED');

    // Query status of the hidden auto-selected handling-party field (regular-user training
    // reports). Owns the visible error/retry affordance and gates report generation (CUR-796).
    const [hiddenPartiesQuery, setHiddenPartiesQuery] = useState<HandlingPartySelectQueryState | null>(null);

    const claimsReportFiltersQuery = trpc.claim.getClaimsColumnsForFiltering.useQuery(
        type === 'CLAIMS' || type === 'CLAIMS_BORDEREAU' || type === 'CLAIMS_GICOP_BREACH' ? undefined : skipToken,
    );

    return (
        <OverlayPage
            title="Generate a new report"
            size="middle"
            afterClose={() => {
                form.resetFields();
                void trpcUtils.report.invalidate();
                navigate({
                    to: '/console/$orgId/reporting',
                    params: {
                        orgId,
                    },
                });
            }}
            footer={({ onClose }) => (
                <>
                    <Button type="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form={formId}
                        loading={isPending}
                        // A regular-user training report auto-selects its (hidden) handling
                        // parties — require both loaded options and a populated form value.
                        disabled={
                            isRegularUserTrainingReport && (!hiddenPartiesQuery?.isReady || !handlingPartyIds?.length)
                        }
                    >
                        Generate report
                    </Button>
                </>
            )}
        >
            {({ onClose }) => (
                <Form
                    id={formId}
                    layout="vertical"
                    form={form}
                    onFinish={(values) => {
                        message.loading('Generating report...');

                        mutate(
                            match(values)
                                .with({ type: 'RISK' }, (value) => ({
                                    type: value.type,
                                }))
                                .with({ type: 'COMPLAINTS_ASIC_2023' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    asicReportingPeriod: value.asicReportingPeriod,
                                }))
                                .with({ type: 'COMPLAINTS' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    dateRange: value.dateRange,
                                }))
                                .with({ type: 'INCIDENTS' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    dateRange: value.dateRange,
                                }))
                                .with({ type: 'IBCCC_BREACH_2025' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    dateRange: value.dateRange,
                                }))
                                .with({ type: 'STAFF_TRAINING_SUMMARY' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    dateRange: value.dateRange,
                                    includeDisabledUsers: value.includeDisabledUsers,
                                }))
                                .with({ type: 'STAFF_TRAINING_DETAILED' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    dateRange: value.dateRange,
                                    includeDisabledUsers: value.includeDisabledUsers,
                                }))
                                .with({ type: 'CLAIMS' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    dateRange: value.dateRange,
                                    productTypeIds: value.productTypeIds,
                                    insurerIds: value.insurerIds,
                                    brokerCompanies: value.brokerCompanies,
                                    supplierIds: value.supplierIds,
                                    statuses: value.statuses,
                                    decisions: value.decisions,
                                    triageOutcomes: value.triageOutcomes,
                                    causesOfLoss: value.causesOfLoss,
                                }))
                                .with({ type: 'CLAIMS_BORDEREAU' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    dateRange: value.dateRange,
                                    productTypeIds: value.productTypeIds,
                                    insurerIds: value.insurerIds,
                                    brokerCompanies: value.brokerCompanies,
                                    supplierIds: value.supplierIds,
                                    statuses: value.statuses,
                                    decisions: value.decisions,
                                    triageOutcomes: value.triageOutcomes,
                                    causesOfLoss: value.causesOfLoss,
                                }))
                                .with({ type: 'CLAIMS_GICOP_BREACH' }, (value) => ({
                                    type: value.type,
                                    handlingPartyIds: value.handlingPartyIds,
                                    dateRange: value.dateRange,
                                    productTypeIds: value.productTypeIds,
                                    insurerIds: value.insurerIds,
                                    brokerCompanies: value.brokerCompanies,
                                    supplierIds: value.supplierIds,
                                    statuses: value.statuses,
                                    decisions: value.decisions,
                                    triageOutcomes: value.triageOutcomes,
                                    causesOfLoss: value.causesOfLoss,
                                }))
                                .exhaustive(),
                            {
                                onSuccess() {
                                    message.success('Report generated');
                                    capture('report_generated', {
                                        report_type: type,
                                        module: REPORT_TYPE_MODULE_MAP[type],
                                    });
                                    onClose();
                                },
                                onError(error) {
                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                >
                    <FormItem
                        label="Report"
                        name="type"
                        rules={[{ required: true }]}
                        extra={
                            type === 'STAFF_TRAINING_SUMMARY'
                                ? 'Only users with training points tracking switched on appear in the staff training summary report. If tracking is switched off for your account, the generated report will contain headers only.'
                                : undefined
                        }
                    >
                        <Select<Report['type']>
                            options={reportTypeOptions}
                            onChange={(value) => {
                                if (value === 'IBCCC_BREACH_2025') {
                                    form.setFieldsValue({
                                        dateRange: [new Date('2025-01-01T00:00:00'), new Date('2025-12-31T00:00:00')],
                                    });
                                }
                            }}
                        />
                    </FormItem>
                    {type === 'COMPLAINTS_ASIC_2023' && (
                        <FormItem
                            label="Reporting period"
                            required
                            name="asicReportingPeriod"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={generateLastTenPeriodsForAsicReport()
                                    .map((item) => ({ value: item }))
                                    .reverse()}
                            />
                        </FormItem>
                    )}
                    {(type === 'CLAIMS' ||
                        type === 'CLAIMS_BORDEREAU' ||
                        type === 'CLAIMS_GICOP_BREACH' ||
                        type === 'COMPLAINTS' ||
                        type === 'INCIDENTS' ||
                        type === 'IBCCC_BREACH_2025' ||
                        type === 'STAFF_TRAINING_SUMMARY' ||
                        type === 'STAFF_TRAINING_DETAILED') && (
                        <FormItem
                            label={
                                type === 'CLAIMS' || type === 'CLAIMS_BORDEREAU' || type === 'CLAIMS_GICOP_BREACH'
                                    ? 'Lodgement date range'
                                    : 'Date range'
                            }
                            name="dateRange"
                            rules={[{ required: true }]}
                        >
                            <RangePicker format={fDateShortDayJs} className="w-full" dateOnly />
                        </FormItem>
                    )}
                    {type && type !== 'RISK' && !isRegularUserTrainingReport && (
                        <Form.Item
                            label={
                                handledNoun ? `Report on ${handledNoun} handled by` : 'Handling parties to report on'
                            }
                            name="handlingPartyIds"
                            preserve={false}
                            rules={[{ required: true }]}
                            extra={`Filters which ${handledNoun ?? 'records'} appear in the report. Nothing is sent to the selected parties.`}
                        >
                            <HandlingPartySelect
                                showUnallocated={!isTrainingManagerOnly}
                                forOrgAdminShowAll
                                forTrainingManagerShowTmParties
                                autoSelectAll
                                canSelectIsDisabledHandlingParty
                                // "Covers records handled by …" reads wrong for staff training reports
                                // (parties filter staff there), so no summary without a bound noun
                                showSummary={!!handledNoun}
                                summaryNoun={handledNoun}
                            />
                        </Form.Item>
                    )}
                    {isRegularUserTrainingReport && (
                        <>
                            {/* The value field stays hidden (regular users don't choose parties), but the
                                query status gets a visible owner below — a failed autoSelectAll otherwise
                                left the required input silently unpopulated (CUR-796). */}
                            <Form.Item name="handlingPartyIds" preserve={false} rules={[{ required: true }]} hidden>
                                <HandlingPartySelect autoSelectAll onQueryStateChange={setHiddenPartiesQuery} />
                            </Form.Item>
                            {!hiddenPartiesQuery?.isReady && hiddenPartiesQuery?.error == null && (
                                <Form.Item label="Report scope">
                                    <Space align="center" role="status" size="small">
                                        {/* curium:allow-spin — inline leaf-node loading indicator. */}
                                        <Spin size="small" />
                                        <span>Loading handling parties...</span>
                                    </Space>
                                </Form.Item>
                            )}
                            {hiddenPartiesQuery?.error != null && !hiddenPartiesQuery.isReady && (
                                <Form.Item label="Report scope">
                                    <InlineQueryError
                                        compact
                                        error={hiddenPartiesQuery.error}
                                        onRetry={hiddenPartiesQuery.retry}
                                        retrying={hiddenPartiesQuery.isRetrying && !hiddenPartiesQuery.canRetry}
                                        title="Handling parties failed to load"
                                    />
                                </Form.Item>
                            )}
                            {hiddenPartiesQuery?.isReady && !handlingPartyIds?.length && (
                                <Form.Item label="Report scope">
                                    <Alert
                                        description="Ask an organisation administrator to assign you to a handling party before generating this report."
                                        showIcon
                                        title="You are not assigned to any handling party"
                                        type="warning"
                                    />
                                </Form.Item>
                            )}
                        </>
                    )}
                    {(type === 'CLAIMS' || type === 'CLAIMS_BORDEREAU' || type === 'CLAIMS_GICOP_BREACH') && (
                        <Row gutter={16}>
                            <Col span={12}>
                                <FormItem label="Product" name="productTypeIds">
                                    <ProductTypeSelect mode="multiple" />
                                </FormItem>
                            </Col>
                            <Col span={12}>
                                <FormItem label="Insurer" name="insurerIds">
                                    <InsurerSelect mode="multiple" />
                                </FormItem>
                            </Col>
                            <Col span={12}>
                                <FormItem label="Broker" name="brokerCompanies">
                                    <Select
                                        loading={claimsReportFiltersQuery.isPending}
                                        mode="multiple"
                                        allowClear
                                        options={claimsReportFiltersQuery.data?.brokerCompanies.map((item) => ({
                                            value: item,
                                        }))}
                                    />
                                </FormItem>
                            </Col>
                            <Col span={12}>
                                <FormItem label="Supplier name" name="supplierIds">
                                    <SupplierSelect mode="multiple" />
                                </FormItem>
                            </Col>
                            <Col span={12}>
                                <FormItem label="Status" name="statuses">
                                    <Select
                                        loading={claimsReportFiltersQuery.isPending}
                                        mode="multiple"
                                        allowClear
                                        options={claimsReportFiltersQuery.data?.statuses.map((item) => ({
                                            value: item,
                                        }))}
                                    />
                                </FormItem>
                            </Col>
                            <Col span={12}>
                                <FormItem label="Decision" name="decisions">
                                    <Select
                                        loading={claimsReportFiltersQuery.isPending}
                                        mode="multiple"
                                        allowClear
                                        options={claimsReportFiltersQuery.data?.decisions.map((item) => ({
                                            value: item,
                                        }))}
                                    />
                                </FormItem>
                            </Col>
                            <Col span={12}>
                                <FormItem label="Triage outcome" name="triageOutcomes">
                                    <Select
                                        loading={claimsReportFiltersQuery.isPending}
                                        mode="multiple"
                                        allowClear
                                        options={claimsReportFiltersQuery.data?.triageOutcomes.map((item) => ({
                                            value: item,
                                        }))}
                                    />
                                </FormItem>
                            </Col>
                            <Col span={12}>
                                <FormItem label="Cause of loss" name="causesOfLoss">
                                    <Select
                                        loading={claimsReportFiltersQuery.isPending}
                                        mode="multiple"
                                        allowClear
                                        options={claimsReportFiltersQuery.data?.causesOfLoss.map((item) => ({
                                            value: item,
                                        }))}
                                    />
                                </FormItem>
                            </Col>
                        </Row>
                    )}
                    {(type === 'STAFF_TRAINING_SUMMARY' || type === 'STAFF_TRAINING_DETAILED') &&
                        (isRegularUserTrainingReport ? (
                            <Form.Item name="includeDisabledUsers" hidden initialValue={true}>
                                <Switch />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label="Include disabled users"
                                name="includeDisabledUsers"
                                layout="horizontal"
                                tooltip="Include users that have been deactivated but may still have training records associated with them"
                                initialValue={true}
                                colon={false}
                            >
                                <Switch />
                            </Form.Item>
                        ))}
                </Form>
            )}
        </OverlayPage>
    );
}
